import React, { useState, useEffect } from 'react';
import { SeekerHeader } from '@ui/components/SeekerHeader';
import { TextInput } from '../ui/TextInput';
import { obfuscateEmail } from '../ui/formStyles';

type OnboardingEmailVerificationStepProps = {
  email: string;
  onSubmit: (code: string) => void | Promise<void>;
  onResend: () => void | Promise<void>;
  onUpdateEmail: () => void;
};

export function OnboardingEmailVerificationStep({
  email,
  onSubmit,
  onResend,
  onUpdateEmail
}: OnboardingEmailVerificationStepProps) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | undefined>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async () => {
    if (submitting) return;
    if (!code.trim()) {
      setError('Please enter the verification code.');
      return;
    }
    if (code.trim().length < 4) {
      setError('Please enter a valid verification code.');
      return;
    }
    setError(undefined);
    setResendMessage(undefined);
    setSubmitting(true);
    try {
      await onSubmit(code.trim());
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Invalid or expired code. Please try again.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (resending) return;
    setError(undefined);
    setResendMessage(undefined);
    setResending(true);
    try {
      await onResend();
      setResendMessage('A new verification code has been sent.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to resend code. Please try again.';
      setError(message);
    } finally {
      setResending(false);
    }
  };

  const obfuscatedEmail = obfuscateEmail(email);

  return (
    <div className="flex min-h-screen w-full flex-col lg:overflow-auto overflow-hidden">
      <div className="relative flex flex-1 w-full flex-col bg-[#fefefe]">
        {/* Mobile header */}
        <header className="flex lg:hidden h-16 shrink-0 items-center border-b border-onboarding-bg/25 bg-onboarding-header px-4">
          <div className="w-10" aria-hidden />
          <div className="flex flex-1 justify-center">
            <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
          </div>
          <div className="w-10" aria-hidden />
        </header>

        {/* Desktop header */}
        <div className="hidden lg:block">
          <SeekerHeader />
        </div>

        {/* Content wrapper */}
        <div className="flex flex-1 flex-col lg:items-center lg:px-[72px] lg:py-[40px] lg:gap-[40px]">
          {/* Main content area */}
          <main className="flex flex-1 lg:flex-initial min-h-0 flex-col items-center gap-[24px] overflow-y-auto lg:overflow-visible px-4 lg:px-0 pb-10 lg:pb-[40px] pt-6 lg:pt-0 w-full lg:w-[509px]">
            {/* Title */}
            <h2 className="font-recoleta text-[32px] text-[#214d3c] tracking-[-0.448px] leading-[44px] w-full">
              We just sent a validation code to {obfuscatedEmail}
            </h2>

            {/* Helper text */}
            <p className="text-[18px] leading-[1.4] text-black w-full">
              You should have received an email from Guides Collective with a verification code. Please enter it below to confirm your email address.
            </p>

            {/* Verification code input */}
            <div className="w-full">
              <TextInput
                id="verificationCode"
                value={code}
                onChange={(value) => {
                  setCode(value);
                  if (error) setError(undefined);
                }}
                placeholder="Enter verification code"
                error={error}
              />
            </div>

            {resendMessage && (
              <p className="text-sm text-onboarding-card-green w-full" role="status">{resendMessage}</p>
            )}

            {/* Action buttons */}
            <div className="flex flex-col gap-[12px] w-full pt-4">
              {/* Submit button - primary */}
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="flex h-[54px] w-full items-center justify-center rounded-full bg-onboarding-card-green text-onboarding-text-light transition-colors hover:bg-onboarding-card-green/90 disabled:opacity-50 disabled:pointer-events-none"
              >
                <span className="text-base font-normal">{submitting ? 'Verifying…' : 'Submit'}</span>
              </button>

              {/* Resend button - outline */}
              <button
                type="button"
                onClick={handleResend}
                disabled={resending || submitting}
                className="flex h-[54px] w-full items-center justify-center rounded-full border border-onboarding-card-green text-onboarding-card-green transition-colors hover:bg-onboarding-card-green/5 disabled:opacity-50 disabled:pointer-events-none"
              >
                <span className="text-base font-normal">{resending ? 'Sending…' : 'Resend verification code'}</span>
              </button>

              {/* Update email button - outline */}
              <button
                type="button"
                onClick={onUpdateEmail}
                className="flex h-[54px] w-full items-center justify-center rounded-full border border-onboarding-card-green text-onboarding-card-green transition-colors hover:bg-onboarding-card-green/5"
              >
                <span className="text-base font-normal">Update email address</span>
              </button>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
